// Single source of truth for the letter assessment order.
// Used by AssessmentForm; Practice and Results still read the legacy field
// and will migrate in a follow-up.
//
// Schema (on user.profile):
//   - assessmentOrder?: { uppercase: string[], lowercase: string[], sounds: string[] }
//       New (v3.4.0). Per-test-type orders. Each array is 26 uppercase letters.
//       The lowercase test renders these letters lowercased; sounds renders
//       them as uppercase glyphs (matching the existing flashcard convention).
//       v3.85.0: a Sounds VARIANT (src/utils/assessmentEntry.js row table)
//       still keys its responses by these uppercase letters — the row's
//       letterCase only changes what the card DRAWS (glyphForCase), and the
//       consonants-only preset is removed at run time (getRunOrder), never
//       stored: the profile always keeps a 26-letter order (isValid26).
//   - linkedAssessmentOrders?: boolean
//       UI state — when true, the three orders are kept in sync and the UI
//       shows a single editor. Doesn't affect read behavior; the helper
//       always reads whichever per-type entry is present.
//   - sequenceType?: 'default' | 'setA' | 'setB' | 'setC' | 'custom'
//   - customLetterOrder?: string[]   LEGACY. Single applies-to-all order.
//       Read as the fallback when assessmentOrder[testType] is missing.
//   - useCustomOrder?: boolean   LEGACY mirror of (sequenceType !== 'default').
//
// Read precedence in getAssessmentOrder:
//   1. profile.assessmentOrder[testType]   (new per-type schema)
//   2. profile.customLetterOrder           (legacy single-order fallback)
//   3. DEFAULT_ORDER                       (built-in default)

import { lettersForRun } from './assessmentEntry';

export const DEFAULT_ORDER = 'MGSIBXLQHWTRJCOVPFDUAYNZKE'.split('');

const isValid26 = (arr) =>
  Array.isArray(arr) && arr.length === 26 && arr.every(l => typeof l === 'string' && /^[A-Za-z]$/.test(l));

/**
 * Returns the letter array to present for a given test type.
 * Always returns 26 letters in the case appropriate for the test:
 *   - uppercase / sounds → uppercase letters
 *   - lowercase          → lowercase letters
 */
export function getAssessmentOrder(profile, testType) {
  // Sounds variants (v3.85.0) share the sounds order — one order, one key set.
  if (testType === 'sounds_lower' || testType === 'sounds_consonants' || testType === 'sounds_both') {
    return getAssessmentOrder(profile, 'sounds');
  }
  const perType = profile?.assessmentOrder?.[testType];
  if (isValid26(perType)) {
    return testType === 'lowercase'
      ? perType.map(l => l.toLowerCase())
      : perType.map(l => l.toUpperCase());
  }

  const legacy = profile?.customLetterOrder;
  const base = isValid26(legacy) ? legacy : DEFAULT_ORDER;
  return testType === 'lowercase'
    ? base.map(l => l.toLowerCase())
    : base.map(l => l.toUpperCase());
}

/**
 * The letters ONE RUN presents (v3.85.0): the teacher's 26-letter order for
 * the row's doc test minus the row's not-administered preset (the consonants-
 * only row drops a e i o u → 21 letters). Keys keep the doc test's case
 * (uppercase for every Sounds row); `letterCase` is display-only and read by
 * the card, never here.
 *
 * @param {object} profile   user.profile
 * @param {{ testType: string, preset?: string[] }} spec  runSpecForKey(...)
 */
export function getRunOrder(profile, spec) {
  const order = getAssessmentOrder(profile, spec?.testType);
  return lettersForRun(order, spec?.preset || []);
}
